import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'

import { Country } from 'constants/country'
import { SignupHowDidYouHearAnswers } from 'constants/signupQuestions'
import constate from 'constate'
import { useCreateUser, useUserMe } from 'dataQueries'
import { Salutation } from 'models/user'
import i18n from 'translations/i18n'
import { Language } from 'translations/Language'
import { logAnalyticsEvent } from 'utils/analytics'
import { useAuth0 } from 'utils/auth'
import { getUserTimezone } from 'utils/time'

export enum WorkspaceType {
  BUSINESS = 'BUSINESS',
  PERSONAL = 'PERSONAL',
}

export const [SignupRegistrationProvider, useSignupRegistration] = constate(() => {
  const { refreshUser } = useAuth0()

  // enabled=false: appMeta owns the /user/me fetch — this observer only READS the shared cache
  // (data for the timezone prefill). Passing enabled here made this mount refetch the errored
  // query, flipping it to pending and bouncing RouteSwitch back to LoadingPage — unmounting this
  // very form in an endless loop (ISSUE-2266). The extra fetches also hammered the BE's Auth0
  // Management lookup into 429 rate limits on dev.
  const userMeQuery = useUserMe(false)

  const i18nLang = useMemo(() => i18n.language.toUpperCase().replace(/-.*/igm, ''), [])

  const [salutation, setSalutation] = useState<Salutation>()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [country, setCountry] = useState<Country>(Country.CH)
  const [phone, setPhone] = useState('')
  const [isPhoneValid, setIsPhoneValid] = useState(false)
  const [phoneErrorMsg, setPhoneErrorMsg] = useState<string>()
  const [timezone, setTimezone] = useState(getUserTimezone)
  const [language, setLanguage] = useState<Language>(Language[i18nLang as Language] ? Language[i18nLang as Language] : Language.EN)
  const [organizationName, setOrganizationName] = useState('')
  const [addressStreet, setAddressStreet] = useState('')
  const [addressZip, setAddressZip] = useState('')
  const [addressCity, setAddressCity] = useState('')
  const [addressCountry, setAddressCountry] = useState<Country>(Country.CH)
  const [howDidYouHear, setHowDidYouHear] = useState('')
  const [workspaceType, setWorkspaceType] = useState<WorkspaceType>(WorkspaceType.BUSINESS)

  const createUser = useCreateUser()
  const isCreateUserProcessed = useMemo(() => createUser.isPending || createUser.isSuccess, [createUser.isPending, createUser.isSuccess])

  const isAccountInfoStepValid = useMemo(() => {
    if (!salutation) return false
    if (!firstName || !lastName) return false
    if (!country) return false
    if (!language) return false
    if (!timezone) return false
    if (!howDidYouHear) return false

    return true
  }, [country, firstName, howDidYouHear, language, lastName, salutation, timezone])

  const isCreateWorkspaceStepValid = useMemo(() => {
    if (workspaceType === WorkspaceType.BUSINESS && !organizationName) return false
    if (!addressStreet || !addressCity || !addressCountry || !addressZip) return false
    if (!phone || !isPhoneValid) return false
    if (isCreateUserProcessed) return false

    return true
  }, [addressCity, addressCountry, addressStreet, addressZip, isCreateUserProcessed, isPhoneValid, organizationName, phone, workspaceType])

  /** Create user action */
  const submitCreateUser = useCallback(() => {

    const onboardingAnswerKey = Object.keys(SignupHowDidYouHearAnswers).find(
      (key) => SignupHowDidYouHearAnswers[key as keyof typeof SignupHowDidYouHearAnswers] === howDidYouHear
    ) as keyof typeof SignupHowDidYouHearAnswers | undefined

    const isBusinessWorkspace = workspaceType === WorkspaceType.BUSINESS

    const fullName = `${firstName} ${lastName}`

    createUser.mutate({
      name: fullName,
      firstName,
      lastName,
      country,
      salutation: salutation!,
      phone,
      mobilePhone: '', // Not used at the moment and not stored in the backend
      locale: {
        language,
        timezone,
      },
      hasOrganization: isBusinessWorkspace,
      organization: isBusinessWorkspace ? {
        name: organizationName,
        billingAddress: `${addressStreet}, ${addressZip} ${addressCity}, ${addressCountry}`,
        hasB2CInvoicing: false,
      } : null,
      isOrganizationCorrect: false,
      invoicingAddress: {
        addressName: isBusinessWorkspace ? organizationName : fullName,
        companyOrIndividualName: isBusinessWorkspace ? organizationName : fullName,
        streetAndNumber: addressStreet,
        postalCode: addressZip,
        city: addressCity,
        country: addressCountry,
      },
      onboardingAnswer: onboardingAnswerKey || undefined,
      refreshUserCallback: refreshUser
    })
  }, [addressCity, addressCountry, addressStreet, addressZip, country, createUser, firstName, howDidYouHear, language, lastName, organizationName, phone, refreshUser, salutation, timezone, workspaceType])

  /** log entering this page */
  useEffect(() => {
    logAnalyticsEvent('enters_user_signup_details_screen', {})
  }, [])

  /** React to userMe request and set default timezone */
  useEffect(() => {
    if (userMeQuery.data) setTimezone(getUserTimezone(userMeQuery.data))
  }, [userMeQuery.data])

  /** Cleanup create user request */
  useLayoutEffect(() => {
    createUser.reset()
    return () => {
      createUser.reset()
    }
    // No dependencies needed to avoid loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    salutation,
    setSalutation,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    country,
    setCountry,
    phone,
    setPhone,
    isPhoneValid,
    setIsPhoneValid,
    timezone,
    setTimezone,
    language,
    setLanguage,
    organizationName,
    setOrganizationName,
    addressStreet,
    setAddressStreet,
    addressZip,
    setAddressZip,
    addressCity,
    setAddressCity,
    addressCountry,
    setAddressCountry,
    howDidYouHear,
    setHowDidYouHear,
    workspaceType,
    setWorkspaceType,
    phoneErrorMsg,
    setPhoneErrorMsg,
    createUser,
    isAccountInfoStepValid,
    isCreateWorkspaceStepValid,
    submitCreateUser,
  }
})
